/**
 * Which language a stretch of text is written in, among the three we have
 * dictionaries for. It's a vote: common little words ("que", "the", "não")
 * and letters only one language uses (ñ, ¿, ã, ç) each count as a point.
 * Short text doesn't get enough votes to call it, and the caller falls back
 * to the document's language.
 */

export type SpellLanguage = 'en' | 'es' | 'pt';

const STOPWORDS: Record<SpellLanguage, Set<string>> = {
    en: new Set(('the and of to is that it you with for this was are have not but what they his her be on ' +
        'at by from i my we an or would there their will can just so if about which when were has had ' +
        'been do does did me your our them he she its who all one how than then into out up more').split(' ')),
    es: new Set(('el la los las de que y a en un una es por con para no se lo del al como más pero sus su le ' +
        'ya o este esta sí porque muy sin sobre también me hay donde quien desde todo nos ni ese eso mi ' +
        'qué yo él ella ustedes son fue era está estoy tengo hace puede todos cuando entonces algo nada ' +
        'eres soy pues aunque así hasta mis tus ellos ellas estos estas otra otro cada vez').split(' ')),
    pt: new Set(('o a os as de que e em um uma é por com para não se do da dos das no na nos nas ao como ' +
        'mais mas seu sua ele ela você isso este esta isto eu meu minha também muito já foi são está tem ' +
        'ou quando pelo pela até há sem então porque ser só aqui onde vocês nós eles elas essa esse ' +
        'tudo nada cada vez minhas meus estou tenho fazer pode').split(' ')),
};

// Letters, or pairs of letters, that only turn up in one of the three.
const SIGNATURES: Array<[RegExp, SpellLanguage, number]> = [
    [/[ñ¿¡]/g, 'es', 2],
    [/[ãõçêôà]/g, 'pt', 2],
    [/ção|ções|nh[aeiou]|lh[aeiou]/g, 'pt', 2],
];

const WORD = /[\p{L}\p{M}]+/gu;

export function detectLanguage(text: string, minScore = 3): SpellLanguage | null {
    const lower = text.toLowerCase();
    const score: Record<SpellLanguage, number> = { en: 0, es: 0, pt: 0 };
    for (const [pattern, lang, weight] of SIGNATURES) {
        score[lang] += (lower.match(pattern)?.length ?? 0) * weight;
    }
    for (const word of lower.match(WORD) ?? []) {
        for (const lang of ['en', 'es', 'pt'] as SpellLanguage[]) {
            if (STOPWORDS[lang].has(word)) score[lang] += 1;
        }
    }
    const ranked = (Object.keys(score) as SpellLanguage[]).sort((a, b) => score[b] - score[a]);
    const [best, second] = ranked;
    if (score[best] < minScore || score[best] === score[second]) return null;
    return best;
}

/** The interface language ('en' | 'es' | 'pt') as a dictionary choice. */
export function toSpellLanguage(code: string | undefined | null): SpellLanguage {
    if (code?.startsWith('es')) return 'es';
    if (code?.startsWith('pt')) return 'pt';
    return 'en';
}

/** For the editor's lang attribute, which screen readers and hyphenation read. */
export const LANG_TAG: Record<SpellLanguage, string> = { en: 'en', es: 'es-419', pt: 'pt-BR' };
